'use client'

import { useState } from "react"
import { BadgeCheck, ChevronRight, Flag, Lock, Phone, Star, Store, Wallet } from "lucide-react"
import { navigateUrl } from "@/utils/client-request"
import { usePathname } from "next/navigation"
import { formatDateNormal, getVendorPrimaryColor, resolveColorHex } from "@/utils/util"
import { Button } from "@/components/elements/Button"
import ReportVendorDialog from "@/components/blocks/ReportVendorDialog"
import Image from "next/image"
import verifyImg from "@/assets/user-verification.png"

export default function Status({ vendor }: { vendor: any }) {
  const verified = !!vendor?.isDanVerified
  if (!verified) return <Unverified vendor={vendor} />
  return <Verified vendor={vendor} />
}

function Verified({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const [reportOpen, setReportOpen] = useState(false)
  const primary = resolveColorHex(getVendorPrimaryColor(vendor))
  const openedDate = vendor?.createdAt ? formatDateNormal(new Date(vendor.createdAt)) : null

  return (
    <div className="bg-white">
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-10 pt-0 md:pt-8 pb-0 md:pb-20">
        <Crumbs vendorName={vendor?.name} verified />

        <div className="mx-auto max-w-190">
        <div className="mt-8 flex items-center gap-5 sm:gap-6">
          <div className="h-20 w-20 sm:h-24 sm:w-24 shrink-0 rounded-full grid place-items-center text-white" style={{ background: primary }}>
            <BadgeCheck className="h-10 w-10 sm:h-12 sm:w-12" strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-emerald-700">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />Баталгаажсан
            </div>
            <h1 className="mt-1.5 font-gilroy text-[28px] sm:text-[38px] font-bold tracking-tight text-foreground leading-[1.05]">
              {vendor?.name}
            </h1>
          </div>
        </div>

        <p className="mt-5 text-[15px] leading-relaxed text-foreground/65 max-w-prose">
          Энэ дэлгүүрийн эзэмшигчийн хувийн мэдээлэл Улсын бүртгэлээр шалгагдан, жинхэнэ иргэн / аж ахуйн нэгж болох нь баталгаажсан.
        </p>

        <div className="mt-10 border-t border-border pt-8">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Бүртгэлийн мэдээлэл</div>
            <div className="text-xs text-emerald-700 inline-flex items-center gap-1.5">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />Идэвхтэй
            </div>
          </div>
          <dl className="mt-4 divide-y divide-border">
            <CertField label="Дэлгүүрийн нэр" value={vendor?.name || '—'} />
            {openedDate && <CertField label="Нээгдсэн огноо" value={openedDate} />}
            <CertField label="Шалгасан байгууллага" value="Улсын бүртгэл (ДАН)" />
          </dl>
        </div>

        <div className="mt-5 flex items-center gap-2 text-xs text-muted-foreground">
          <Lock className="h-3.5 w-3.5 shrink-0" />Эзэмшигчийн хувийн мэдээлэл нийтэд харагдахгүй ба бид аюулгүй хамгаална.
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <Button tag="a" href={navigateUrl('/', pathname)} icon={Store} className="w-full sm:w-auto text-white!" style={{ background: primary }}>Дэлгүүр үзэх</Button>
          <Button type="button" variant="outline" onClick={() => setReportOpen(true)} icon={Flag} className="w-full sm:w-auto">Дэлгүүрийг мэдэгдэх</Button>
        </div>

        <p className="mt-10 text-xs text-muted-foreground leading-relaxed border-t border-border pt-5">
          Энэ хуудас нь зөвхөн мэдээлэл өгөх зорилготой бөгөөд худалдан авалтын аливаа баталгаа болохгүй.
        </p>
        </div>
      </div>
      <ReportVendorDialog open={reportOpen} onClose={() => setReportOpen(false)} vendorId={vendor?.id} vendorName={vendor?.name} />
    </div>
  )
}

function Unverified({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const [reportOpen, setReportOpen] = useState(false)

  const checks = [
    { icon: Wallet, t: 'Төлбөрөө хамгаалалттай аргаар хий', s: 'Боломжтой бол QPay, StorePay зэрэг буцаалт хийх боломжтой аргыг сонго.' },
    { icon: Star, t: 'Бусдын үнэлгээг уншаарай', s: 'Бүтээгдэхүүний хуудсан дахь бодит сэтгэгдэл, үнэлгээг нягтал.' },
    { icon: Phone, t: 'Холбоо барих мэдээллийг шалга', s: 'Утас, имэйл, хаяг бодитоор ажиллаж байгаа эсэхийг нягтал.' },
    { icon: Flag, t: 'Сэжигтэй бол мэдэгд', s: 'Хуурамч дэлгүүр гэж бодож байвал бидэнд шууд мэдэгдээрэй.' },
  ]

  return (
    <div className="bg-white">
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-10 pt-0 md:pt-8 pb-0 md:pb-20">
        <Crumbs vendorName={vendor?.name} verified={false} />

        <div className="mx-auto max-w-190">
        <div className="mt-8 flex items-center gap-5 sm:gap-6">
          <Image src={verifyImg} alt="" aria-hidden className="h-24 w-24 sm:h-28 sm:w-28 shrink-0 select-none" />
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#C2453F]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#E8635F]" />Баталгаажаагүй
            </div>
            <h1 className="mt-1.5 font-gilroy text-[28px] sm:text-[38px] font-bold tracking-tight text-foreground leading-[1.05]">
              {vendor?.name}
            </h1>
          </div>
        </div>

        <p className="mt-5 text-[15px] leading-relaxed text-foreground/65 max-w-prose">
          Энэ дэлгүүрийн эзэмшигчийн хувийн мэдээлэл Улсын бүртгэлээр баталгаажаагүй байна. Худалдан авалт хийхээсээ өмнө доорх зүйлсийг нягтлаарай.
        </p>

        <div className="mt-10 border-t border-border pt-8">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Худалдан авахаасаа өмнө</div>
          <ul className="mt-5 space-y-5">
            {checks.map((c, i) => {
              const Icon = c.icon
              return (
                <li key={i} className="flex gap-4">
                  <div className="mt-0.5 h-8 w-8 shrink-0 grid place-items-center rounded-full bg-[#FCECEC] text-[#C2453F]">
                    <Icon className="h-4 w-4" strokeWidth={2} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[15px] font-semibold text-foreground">{c.t}</div>
                    <div className="mt-0.5 text-[13.5px] leading-relaxed text-muted-foreground">{c.s}</div>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <Button tag="a" href={navigateUrl('/', pathname)} icon={Store} className="bg-foreground text-white! hover:opacity-90 w-full sm:w-auto">Дэлгүүр үзэх</Button>
          <Button type="button" variant="outline" onClick={() => setReportOpen(true)} icon={Flag} className="w-full sm:w-auto">Дэлгүүрийг мэдэгдэх</Button>
        </div>

        <p className="mt-10 text-xs text-muted-foreground leading-relaxed border-t border-border pt-5">
          Энэ хуудас нь зөвхөн мэдээлэл өгөх зорилготой бөгөөд худалдан авалтын аливаа баталгаа болохгүй.
        </p>
        </div>
      </div>
      <ReportVendorDialog open={reportOpen} onClose={() => setReportOpen(false)} vendorId={vendor?.id} vendorName={vendor?.name} />
    </div>
  )
}

function Crumbs({ vendorName, verified }: { vendorName?: string, verified: boolean }) {
  const pathname = usePathname()
  return (
    <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground">
      <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
      <ChevronRight className="h-3 w-3 opacity-50" />
      <a href={navigateUrl('/', pathname)} className="hover:text-foreground">{vendorName || 'Дэлгүүр'}</a>
      <ChevronRight className="h-3 w-3 opacity-50" />
      <span className="text-foreground">{verified ? 'Баталгаажсан төлөв' : 'Баталгаажаагүй төлөв'}</span>
    </div>
  )
}

function CertField({ label, value }: { label: string, value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-3 first:pt-0 last:pb-0">
      <dt className="text-sm text-muted-foreground shrink-0">{label}</dt>
      <dd className="text-sm font-medium text-foreground text-right">{value}</dd>
    </div>
  )
}
